import React, { useCallback, useEffect, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import { Close, ExpandMore, Refresh, Token as TokenIcon } from '@mui/icons-material';
import { fetchAgentTokenUsage } from '../../services/tokenUsageApi';

const DRAWER_WIDTH = 780;

const formatTs = (val) => {
  if (!val) return '—';
  try {
    return new Date(val).toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit',
    });
  } catch {
    return String(val);
  }
};

const formatNum = (n) => {
  if (n == null || n === '') return '—';
  const num = Number(n);
  if (Number.isNaN(num)) return String(n);
  return num.toLocaleString();
};

const shortId = (id) => {
  if (!id) return '—';
  const s = String(id);
  return s.length > 12 ? `${s.slice(0, 8)}…` : s;
};

const SEGMENT_ORDER = ['system', 'context', 'history', 'question', 'tools', 'other', 'response'];

const segmentLabel = (name) => {
  const labels = {
    system: 'System prompt',
    context: 'Context',
    history: 'History',
    question: 'Question',
    tools: 'Tools',
    other: 'Other',
    response: 'Response',
  };
  return labels[name] || name || 'Other';
};

const ContentBlock = ({ label, value }) => {
  if (value == null || value === '') return null;
  return (
    <Box sx={{ mb: 1.5 }}>
      <Typography variant="caption" fontWeight={600} display="block" sx={{ mb: 0.5 }}>
        {label}
      </Typography>
      <Typography
        component="pre"
        variant="caption"
        sx={{
          m: 0,
          p: 1.5,
          bgcolor: 'grey.50',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          overflow: 'auto',
          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
        }}
      >
        {String(value)}
      </Typography>
    </Box>
  );
};

const AgentTokenUsageDrawer = ({ open, onClose, tenantId, agent }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [summary, setSummary] = useState(null);
  const [records, setRecords] = useState([]);

  const agentId =
    agent?.tenantAgentInstanceId ?? agent?.agentUniqueId ?? agent?.agentName ?? null;

  const load = useCallback(async () => {
    if (!agentId || !tenantId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await fetchAgentTokenUsage(agentId, { limit: 200 });
      setSummary(data?.summary || null);
      setRecords(Array.isArray(data?.records) ? data.records : []);
    } catch (err) {
      console.error('Load token usage error:', err);
      setError(err.message || 'Failed to load token usage');
      setSummary(null);
      setRecords([]);
    } finally {
      setLoading(false);
    }
  }, [agentId, tenantId]);

  useEffect(() => {
    if (open && agentId && tenantId) {
      load();
    } else if (!open) {
      setError(null);
      setSummary(null);
      setRecords([]);
    }
  }, [open, agentId, tenantId, load]);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: DRAWER_WIDTH },
          maxWidth: '100%',
        },
      }}
    >
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Box
          sx={{
            p: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: 1,
            borderColor: 'divider',
            gap: 1,
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
            <TokenIcon color="primary" />
            <Typography variant="h6" noWrap>
              Token Usage{agent?.agentName ? ` — ${agent.agentName}` : ''}
            </Typography>
          </Stack>
          <Stack direction="row" spacing={0.5}>
            <IconButton onClick={load} size="small" disabled={loading} title="Refresh">
              <Refresh fontSize="small" />
            </IconButton>
            <IconButton onClick={onClose} size="small">
              <Close />
            </IconButton>
          </Stack>
        </Box>

        <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              {error && (
                <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              {!agentId && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  This agent has no instance id; token usage cannot be loaded.
                </Alert>
              )}

              {summary && (
                <Box
                  sx={{
                    mb: 2,
                    p: 1.5,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    bgcolor: 'grey.50',
                  }}
                >
                  <Typography variant="subtitle2" gutterBottom>
                    Summary (loaded records)
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    <Chip size="small" label={`Calls: ${formatNum(summary.call_count)}`} />
                    <Chip size="small" label={`Prompt: ${formatNum(summary.prompt_tokens)}`} />
                    <Chip size="small" label={`Completion: ${formatNum(summary.completion_tokens)}`} />
                    {summary.cached_tokens != null && Number(summary.cached_tokens) > 0 && (
                      <Chip
                        size="small"
                        color="success"
                        variant="outlined"
                        label={`Cached: ${formatNum(summary.cached_tokens)}`}
                      />
                    )}
                    <Chip
                      size="small"
                      color="primary"
                      label={`Total: ${formatNum(summary.total_tokens)}`}
                    />
                  </Stack>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                    Each row is one LLM call. Expand a row to see token counts and full text for
                    system prompt, context, history, question, tools, and response. Cached tokens
                    (when the provider reports them) mean Call 2…N reused the system prefix at a
                    lower rate — prompt tokens can still look large.
                  </Typography>
                </Box>
              )}

              {!loading && records.length === 0 && !error && agentId && (
                <Alert severity="info">
                  No token usage recorded for this agent yet. Run a chat or task, then refresh.
                </Alert>
              )}

              <Stack spacing={1}>
                {records.map((rec) => {
                  const segments = Array.isArray(rec.segments) ? [...rec.segments] : [];
                  segments.sort((a, b) => {
                    const ai = SEGMENT_ORDER.indexOf(a?.name);
                    const bi = SEGMENT_ORDER.indexOf(b?.name);
                    return (ai < 0 ? 99 : ai) - (bi < 0 ? 99 : bi);
                  });
                  const cachedTokens =
                    rec.cached_tokens ?? rec.details?.cached_tokens ?? null;
                  const key = rec.log_id || `${rec.request_id}-${rec.step_no}-${rec.cycle}`;
                  return (
                    <Accordion key={key} disableGutters elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
                      <AccordionSummary expandIcon={<ExpandMore />}>
                        <Box sx={{ width: '100%', pr: 1 }}>
                          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                            <Typography variant="body2" fontWeight={600}>
                              {rec.cycle != null ? `Call ${rec.cycle}` : rec.scope === 'call' ? 'Call' : 'Run'}
                            </Typography>
                            {rec.model_name && (
                              <Chip size="small" variant="outlined" label={rec.model_name} />
                            )}
                            {rec.usage_source && (
                              <Chip size="small" variant="outlined" label={rec.usage_source} />
                            )}
                            <Chip size="small" label={`in ${formatNum(rec.prompt_tokens)}`} />
                            <Chip size="small" label={`out ${formatNum(rec.completion_tokens)}`} />
                            {cachedTokens != null && Number(cachedTokens) > 0 && (
                              <Chip
                                size="small"
                                color="success"
                                variant="outlined"
                                label={`cached ${formatNum(cachedTokens)}`}
                              />
                            )}
                            <Chip size="small" color="primary" label={`Σ ${formatNum(rec.total_tokens)}`} />
                          </Stack>
                          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                            {formatTs(rec.started_at || rec.created_at)} · request {shortId(rec.request_id)}
                            {rec.request_category ? ` · ${rec.request_category}` : ''}
                          </Typography>
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails sx={{ pt: 0 }}>
                        <Divider sx={{ mb: 1.5 }} />
                        {segments.length === 0 ? (
                          <Typography variant="body2" color="text.secondary">
                            No segment breakdown stored for this record (legacy aggregate). Prompt{' '}
                            {formatNum(rec.prompt_tokens)} / completion {formatNum(rec.completion_tokens)}.
                          </Typography>
                        ) : (
                          segments.map((seg, idx) => (
                            <Box key={`${seg.name}-${idx}`} sx={{ mb: 2 }}>
                              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                                <Typography variant="subtitle2">{segmentLabel(seg.name)}</Typography>
                                <Chip size="small" label={`${formatNum(seg.tokens)} tokens`} />
                                <Chip size="small" variant="outlined" label={`${formatNum(seg.char_count)} chars`} />
                              </Stack>
                              <ContentBlock label="Content" value={seg.content} />
                            </Box>
                          ))
                        )}
                        {(rec.provider_prompt_tokens != null ||
                          rec.estimated_prompt_tokens != null ||
                          (cachedTokens != null && Number(cachedTokens) > 0)) && (
                          <Box sx={{ mt: 1, p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                            <Typography variant="caption" color="text.secondary" display="block">
                              Provider prompt: {formatNum(rec.provider_prompt_tokens)} · estimated prompt:{' '}
                              {formatNum(rec.estimated_prompt_tokens)} · provider completion:{' '}
                              {formatNum(rec.provider_completion_tokens)} · estimated completion:{' '}
                              {formatNum(rec.estimated_completion_tokens)}
                              {cachedTokens != null && Number(cachedTokens) > 0
                                ? ` · cached: ${formatNum(cachedTokens)}`
                                : ''}
                            </Typography>
                          </Box>
                        )}
                      </AccordionDetails>
                    </Accordion>
                  );
                })}
              </Stack>
            </>
          )}
        </Box>
      </Box>
    </Drawer>
  );
};

export default AgentTokenUsageDrawer;
